import addressApi from "api/landlord/address";
import { PropertyKind } from "shared/types";

const PER_PAGE = 1000;
const PAGE_CONCURRENCY = 6;

/**
 * The slice of a unit a listing picker renders. Deliberately narrower than `Unit` —
 * it mirrors the API's `view=picker` payload, which drops everything the units table
 * needs and a checkbox list does not.
 */
export interface PickerUnit {
  id: number;
  title: string | null;
  listed: boolean;
  monthly_rent_amount: { cents: number; currency: string } | null;
  property: {
    id: number;
    name: string;
    kind: PropertyKind | null;
    property_group: { id: number; name: string; index: number } | null;
  } | null;
}

interface Page<T> {
  items: T[];
  page_count?: number;
}

/**
 * Reads page 1, then fetches the rest in bounded-concurrency waves. Callers used to
 * request a single oversized page and silently keep whatever the API's per-page ceiling
 * returned, which cut portfolios off mid-list.
 */
export const fetchAllPages = async <T>(
  fetchPage: (page: number) => Promise<Page<T>>
): Promise<T[]> => {
  const firstPage = await fetchPage(1);
  const pageCount = firstPage.page_count ?? 1;
  if (pageCount <= 1) return firstPage.items;

  const items = [...firstPage.items];
  const remaining = Array.from(
    { length: pageCount - 1 },
    (_, index) => index + 2
  );

  for (let offset = 0; offset < remaining.length; offset += PAGE_CONCURRENCY) {
    const pages = await Promise.all(
      remaining
        .slice(offset, offset + PAGE_CONCURRENCY)
        .map((page) => fetchPage(page))
    );
    pages.forEach((page) => items.push(...page.items));
  }

  return items;
};

export const fetchAllPickerUnits = (): Promise<PickerUnit[]> =>
  fetchAllPages<PickerUnit>((page) =>
    addressApi.getPickerUnits({ page, per_page: PER_PAGE })
  );

export const isListableUnit = (unit: PickerUnit) =>
  Boolean(unit.listed) && (unit.monthly_rent_amount?.cents ?? 0) > 0;
